<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle, useBreakpoints, breakpointsTailwind } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import EvolutionHistorySyncModal from './EvolutionHistorySyncModal.vue';
import EvolutionAddGroupParticipantsDialog from './EvolutionAddGroupParticipantsDialog.vue';
import BlockContactDialog from 'dashboard/components-next/Contacts/BlockContactDialog.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showEvolutionSyncModal, toggleEvolutionSyncModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const addGroupParticipantsDialogRef = ref(null);
const blockContactDialogRef = ref(null);
const isBlockingContact = ref(false);

const currentChat = computed(() => store.getters.getSelectedChat);

const { updateUISettings } = useUISettings();
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// Below xl the side panel switch is hidden, so its panels are reachable from
// here instead.
const breakpoints = useBreakpoints(breakpointsTailwind);
const isXlScreen = breakpoints.greaterOrEqual('xl');
const showCopilotPanel = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.CAPTAIN)
);

const isEvolutionWhatsAppConversation = computed(() => {
  const inbox = store.getters['inboxes/getInbox'](currentChat.value.inbox_id);
  return (
    inbox?.channel_type === 'Channel::Whatsapp' &&
    inbox?.provider === 'evolution'
  );
});

const isEvolutionGroupConversation = computed(
  () =>
    isEvolutionWhatsAppConversation.value &&
    !!currentChat.value.additional_attributes?.is_group
);

const actionMenuItems = computed(() => {
  const items = [];

  if (!isXlScreen.value) {
    items.push({
      icon: 'i-lucide-user',
      label: t('CONVERSATION.SIDEBAR.CONTACT'),
      action: 'show_contact_panel',
      value: 'show_contact_panel',
    });

    if (showCopilotPanel.value) {
      items.push({
        icon: 'i-lucide-sparkles',
        label: t('CONVERSATION.SIDEBAR.COPILOT'),
        action: 'show_copilot_panel',
        value: 'show_copilot_panel',
      });
    }
  }

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  if (isEvolutionWhatsAppConversation.value) {
    items.push({
      icon: 'i-lucide-history',
      label: t('CONTACT_PANEL.SYNC_EVOLUTION_HISTORY'),
      action: 'sync_evolution_history',
      value: 'sync_evolution_history',
    });
  }

  if (isEvolutionGroupConversation.value) {
    items.push({
      icon: 'i-lucide-user-plus',
      label: t('CONTACT_PANEL.ADD_EVOLUTION_GROUP_PARTICIPANTS.MENU_LABEL'),
      action: 'add_evolution_group_participants',
      value: 'add_evolution_group_participants',
    });
  }

  return items;
});

// Bloquear pergunta qual dos tipos antes; desbloquear é uma ação só.
const mute = () => blockContactDialogRef.value?.open();

const handleBlockContact = async blockMode => {
  isBlockingContact.value = true;
  await store.dispatch('muteConversation', {
    conversationId: currentChat.value.id,
    blockMode,
  });
  isBlockingContact.value = false;
  blockContactDialogRef.value?.close();
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'show_contact_panel') {
    updateUISettings({
      is_contact_sidebar_open: true,
      is_copilot_panel_open: false,
    });
  } else if (action === 'show_copilot_panel') {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: true,
    });
  } else if (action === 'mute') {
    mute();
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'sync_evolution_history') {
    toggleEvolutionSyncModal();
  } else if (action === 'add_evolution_group_participants') {
    addGroupParticipantsDialogRef.value?.open();
  }
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <EvolutionHistorySyncModal
      v-if="showEvolutionSyncModal"
      :show="showEvolutionSyncModal"
      :conversation-id="currentChat.id"
      @cancel="toggleEvolutionSyncModal"
    />
    <EvolutionAddGroupParticipantsDialog
      v-if="isEvolutionGroupConversation"
      ref="addGroupParticipantsDialogRef"
      :conversation-id="currentChat.id"
    />
    <BlockContactDialog
      ref="blockContactDialogRef"
      :is-updating="isBlockingContact"
      @block="handleBlockContact"
    />
  </div>
</template>
